import { DUMMY_LOGGER } from 'UTILS/constants';
import { stopStream } from 'UTILS/stream';

/**
 * Checks a series of resolutions with a specific camera. Resolves with the supported resolutions.
 *
 * @param {MediaDeviceInfo.deviceId} deviceId - The deviceId of the camera to check.
 * @param {Array<number>} resolutions - The list of resolutions to be tested. It is supposed to be ordered by ascending size.
 * @param {{warn: function, debug: function, log: function, error: function, info: function}} [logger] - The logger to be used.
 * @returns {Promise<[<number>]>} - The list of supported resolutions.
 */
export const getSupportedResolutions = async({
  deviceId,
  resolutions,
  logger = DUMMY_LOGGER,
}) => {
  const resolutionsToCheck = [...resolutions];
  const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId } });
  const videoTrack = stream?.getVideoTracks()?.[0];
  if (!videoTrack) {
    logger.error('Video stream failed');
    throw new Error('no_video_track_available');
  }
  let resolution;
  while (resolutionsToCheck.length) {
    resolution = resolutionsToCheck.shift();
    try {
      await videoTrack.applyConstraints({
        deviceId,
        height: {
          min: resolution,
        },
      });
      logger.log('Resolution', resolution, 'is supported.');
      stopStream(stream);
      return resolution;
    } catch (err) {
      if (err.name === 'OverconstrainedError' || err.constructor.name === 'OverconstrainedError') {
        logger.log('Resolution', resolution, 'is not supported.');
      } else {
        stopStream(stream);
        throw err;
      }
    }
  }
};
